// src/pages/AccountPage.tsx — Nexus-themed account info
// Replaces admin/src/pages/AccountPage.tsx
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { SessionInfo } from '@eotir/oidcbridge-shared/api-contracts';

export function AccountPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => api<SessionInfo>('/api/me', { signal }),
  });

  return (
    <div>
      <div className="eo-eyebrow">Citizen Identity</div>
      <h1 className="eo-h1 mb-6">Account</h1>
      {isLoading && <p className="text-sm text-ink-3">Loading…</p>}
      {error && <p className="text-sm text-err">Failed to load: {(error as Error).message}</p>}
      {data && (
        <div className="eo-card max-w-2xl">
          <dl className="grid grid-cols-[180px_1fr] gap-y-3 text-[13px]">
            <dt className="eo-label">Name</dt>
            <dd>{data.name}</dd>
            <dt className="eo-label">Email</dt>
            {/* Members without a forum email can still log in — show a placeholder. */}
            <dd className="font-mono">{data.email ?? <span className="text-ink-3">none on file</span>}</dd>
            <dt className="eo-label">Forum member ID</dt>
            <dd className="font-mono">{data.forumMemberId}</dd>
            <dt className="eo-label">Primary group</dt>
            <dd className="font-mono">{data.primaryGroup}</dd>
            <dt className="eo-label">Subject (sub)</dt>
            <dd className="font-mono text-[11px] break-all text-ink-3">{data.sub}</dd>
            <dt className="eo-label">Access level</dt>
            <dd>
              {data.isSuperAdmin ? (
                <span className="eo-chip eo-chip--warn">Super-admin (env bootstrap)</span>
              ) : (
                <div className="font-mono text-[11.5px] space-y-0.5">
                  {Object.entries(data.permissions).length === 0 && (
                    <span className="text-ink-3">no permissions</span>
                  )}
                  {Object.entries(data.permissions).map(([resource, actions]) => (
                    <div key={resource}>
                      <span className="text-ink-2">{resource}</span>
                      <span className="text-ink-4">: </span>
                      <span className="text-ok-soft">{actions}</span>
                    </div>
                  ))}
                </div>
              )}
            </dd>
          </dl>
          <div className="mt-5 pt-4 border-t border-line-soft">
            <a href="/api/auth/logout" className="eo-btn eo-btn--ghost">Sign out</a>
          </div>
        </div>
      )}
    </div>
  );
}
